import { styled } from '@mui/material/styles';
import { Box, Button, Drawer, TextField, Typography } from '@mui/material';
import { StyledProps, SupportCardProps } from '../types/interface';

interface LoginWrapperProps {
  isDesktop: boolean;
}

export const InlineContainer = styled(Box)(
  ({ theme, justifyContent, alignItems, marginTop, display }) => ({
    width: '100%',

    display: `${display || 'inline-flex'}`,
    justifyContent: `${justifyContent || 'center'}`,
    alignItems: `${alignItems || 'center'}`,

    marginTop: `${marginTop || 'unset'}`,
  })
);

export const LoginWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDesktop',
})<LoginWrapperProps>(({ theme, justifyContent, alignItems, marginTop, isDesktop }) => ({
  display: `${isDesktop ? 'inline-flex' : 'grid'}`,
  width: `${isDesktop ? '90vw' : '100%'}`,
  maxWidth: `${isDesktop ? '90%' : 'unset'}`,

  justifyContent: `${isDesktop ? 'space-between' : 'center'}`,
  alignItems: 'center',
  margin: '10px 0px',
  rowGap: '15px',
}));

export const WidgetHeader = styled(Box)(({ theme }) => ({
  width: '100%',

  display: 'inline-flex',
}));

export const MJMLogo = styled('img')(({ theme }) => ({
  height: 'auto',
  padding: '0px 15px',
  [theme.breakpoints.down('md')]: {
    width: '275px',
    [theme.breakpoints.down('sm')]: {
      width: '160px',
    },
  },
}));

export const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'var(--primary-color)',
  fontFamily: 'Nunito',
  textTransform: 'capitalize',
  padding: '14px 35px',
  borderRadius: '20px',
  '@media (max-resolution: 1dppx) and (min-height: 1030px)': {
    fontSize: '1.30em',
  },
}));

export const SecondaryButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Nunito',
  textTransform: 'capitalize',
  padding: '14px 35px',
  borderRadius: '20px',
  marginLeft: '1.5em',
  color: 'theme.text.primary',
  border: '2px solid var(--primary-color)',
  '&:hover': {
    border: '2px solid var(--primary-color)',
    color: 'var(--font-color)',
  },
  '@media (max-resolution: 1dppx) and (min-height: 1030px)': {
    fontSize: '1.30em',
    border: '2px solid var(--primary-color)',
  },
}));

export const NavigationContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 'fit-content',
  padding: '10px 0',

  [theme.breakpoints.up('sm')]: {
    width: '90%',
    margin: '10px 0',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  [theme.breakpoints.up('md')]: {
    width: '100%',
    margin: '0px',
    marginBottom: '10px',
  },
}));

export const TopNavIcon = styled('img')(({ theme }) => ({
  width: '35px',
  height: '35px',

  padding: '4px 8px',

  borderRadius: '10px',
  border: `1px solid ${theme.palette.common.white}`,

  background: 'white',

  '&:hover': {
    background: '#69697a17',
  },

  [theme.breakpoints.down('md')]: {
    height: '45px',
    width: '45px',
  },
}));

export const BackdropOverlay = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  width: '100vw',
  height: '100vh',

  justifyContent: 'center',
  alignItems: 'center',

  position: 'absolute',
  top: '0',
  left: '0',

  background: '#00000023',

  zIndex: 5,

  [theme.breakpoints.up('md')]: {
    zIndex: 1,
  },
}));

export const AppContainer = styled(Box)(({ theme }) => ({
  width: '100vw',
  alignItems: 'center',
  margin: '0px auto',

  [theme.breakpoints.between('xs', 'md')]: {
    width: '100vw',
    padding: '1em',
  },

  [theme.breakpoints.up('lg')]: {},
}));

export const BackDrop = styled(Box)(({ theme, display, height }) => ({
  width: '100%',
  overflow: 'auto',
  [`&::-webkit-scrollbar`]: {
    width: '7.5px',
    height: '0px',
  },

  [`&::-webkit-scrollbar-track`]: {
    background: '#ededed',
    borderRadius: '15px',
  },

  [`&::-webkit-scrollbar-thumb`]: {
    background: '#888',
    borderRadius: '15px',
  },

  [`&::-webkit-scrollbar-thumb:hover`]: {
    background: '#555',
  },

  [`&::-webkit-scrollbar-button:end`]: {
    height: '5px',
    width: '30%',
  },
  [`&::-webkit-scrollbar-button:start`]: {
    height: '5px',
    width: '30%',
  },
  minHeight: `${height ? `calc(${height}px * 0.90)` : '82vh'}`,
  display: `${display || 'block'}`,
  justifyContent: 'center',
  alignItems: 'center',

  backgroundColor: `${theme.palette.background.paper}`,
  border: `1px solid ${theme.palette.primary.dark}`,
  borderRadius: '20px',

  padding: '1em',
  [theme.breakpoints.down('md')]: {
    height: 'max-content',

    '@media (min-height: 500px)': { minHeight: '73vh' },
    '@media (min-height: 550px)': { minHeight: '76.5vh' },
    '@media (min-height: 650px)': { minHeight: '79vh' },
    '@media (min-height: 750px)': { minHeight: '81.5vh' },
    '@media (min-height: 850px)': { minHeight: '82vh' },
    '@media (min-height: 950px)': { minHeight: '80vh' },
    '@media (min-height: 1000px)': { minHeight: '78vh' },
  },
  '@media (min-resolution: 1.49dppx)': {
    '@media (max-height: 700px)': {},
  },
}));

export const ProfileBackDrop = styled(Box)(({ theme, display, height }) => ({
  display: `${display || 'block'}`,
  justifyContent: 'center',
  alignItems: 'center',

  width: '100%',
  borderRadius: '20px',

  [theme.breakpoints.down('md')]: {
    height: '73vh',

    '@media (min-height: 500px)': { height: '73vh' },
    '@media (min-height: 550px)': { height: '76.5vh' },
    '@media (min-height: 650px)': { height: '79vh' },
    '@media (min-height: 750px)': { height: '81.5vh' },
    '@media (min-height: 850px)': { height: '82vh' },
    '@media (min-height: 950px)': { height: '80vh' },
    '@media (min-height: 1000px)': { height: '78vh' },
    backgroundColor: `${theme.palette.background.paper}`,
    border: `1px solid ${theme.palette.primary.dark}`,
  },
  [theme.breakpoints.up('md')]: {
    height: '100%',
    border: 'none',
    backgroundColor: 'none',
  },

  '@media (min-resolution: 1.49dppx)': {
    '@media (max-height: 700px)': {},
  },
}));

export const EntryBackDrop = styled(BackDrop)(({ theme, display, height }) => ({
  width: 'inherit',
  maxWidth: '100%',
  minHeight: 'fit-content !important',
  [theme.breakpoints.up('sm')]: {},
  [theme.breakpoints.up('md')]: {
    maxWidth: '650px',
    padding: '30px',
    '& > div': {
      justifyContent: 'center',
    },
  },
}));

export const RegistrationBackDrop = styled(BackDrop)(({ theme }) => ({
  height: 'max-content !important',
  maxWidth: '500px',
  padding: '1em',
  margin: '0 auto',
  [theme.breakpoints.up('md')]: {
    padding: '2em',
  },
}));

export const QuizBackDrop = styled(BackDrop)(({ theme, display, height }) => ({
  maxWidth: '100vw',
  textAlign: 'unset',
  padding: '2em',

  [theme.breakpoints.up('sm')]: {
    padding: '1em',
    maxWidth: '80vw',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 'unset',
    textAlign: 'center',
    padding: '1em',
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '95vw !important',
    '@media (min-height: 500px)': { height: '73vh' },
    '@media (min-height: 550px)': { height: '76.5vh' },
    '@media (min-height: 650px)': { height: '79vh' },
    '@media (min-height: 750px)': { height: '81.5vh' },
    '@media (min-height: 850px)': { height: '82vh' },
    '@media (min-height: 950px)': { height: '80vh' },
    '@media (min-height: 1000px)': { height: '78vh' },
  },
}));

export const CenteredFullBackDrop = styled(BackDrop)(({ theme, display }) => ({
  width: '96vw', // see CenterFullOverlay styled component for full styling
  height: '96vh',
}));

export const CenterMedBackdrop = styled(BackDrop)(({ theme }) => ({
  width: '96vw',
  height: '40vh',

  [theme.breakpoints.up('sm')]: {
    width: '60vw',
  },
  [theme.breakpoints.up('md')]: {
    width: '60vw',
    height: '50vh',
  },
  [theme.breakpoints.up('lg')]: {},
}));

export const CustomCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hideBorder',
})<SupportCardProps>(({ theme, border, hideBorder, centerText }) => ({
  borderRadius: '25px',
  border: `${hideBorder ? 'none' : '2px solid var( --background)'}`,
  color: 'text.primary',
}));

export const BorderBox = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '77vh',
  overflowX: 'hidden',

  border: `2px solid ${theme.palette.primary.dark}`,
  borderRadius: '20px',

  overflowY: 'scroll',

  '@media (min-width: 1200px) and (max-width: 1300px)': {
    height: '72vh',
  },

  '@media(min-width: 1025px) and (max-width: 1112px) and (max-height: 740px)': {
    height: '71.5vh',
  },

  '@media (max-width: 376px)': {
    height: '67vh!important',
  },

  [theme.breakpoints.down('sm')]: {
    height: '73vh',
    marginTop: 'unset',
  },

  [`&::-webkit-scrollbar`]: {
    width: '5px',
    marginRight: '10px!important',
  },

  [`&::-webkit-scrollbar-track`]: {
    background: '#ededed',
    borderRadius: '15px',
  },

  [`&::-webkit-scrollbar-thumb`]: {
    background: '#888',
    borderRadius: '15px',
  },

  [`&::-webkit-scrollbar-thumb:hover`]: {
    background: '#555',
  },

  [`&::-webkit-scrollbar-button:end`]: {
    height: '20px',
    width: '30%',
  },
  [`&::-webkit-scrollbar-button:start`]: {
    height: '20px',
    width: '30%',
  },

  '@media (min-width:768px) and (max-width:1025px)': {
    height: '72vh',
  },
}));

export const JobGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLoading',
})<StyledProps>(({ theme, height, isLoading }) => ({
  width: '100%',
  // height: `calc(${height}px * 0.6)`,
  height: 'fit-content',

  display: 'inline-grid',
  justifyItems: 'center',
  alignItems: `${isLoading ? 'center' : 'flex-start'}`,

  marginTop: '1.5em',

  [theme.breakpoints.down('sm')]: {
    gridGap: '1.5em',
    gridTemplateColumns: '100%',
  },

  [theme.breakpoints.between('sm', 'lg')]: {
    gridTemplateColumns: '1fr 1fr',
    gridGap: '2em',
  },

  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '1fr 1fr 1fr',
    gridGap: '1em',
  },

  '@media (min-width:1700px)': {
    gridGap: '3em',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
  },
}));

export const FilterButton = styled(Button)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 900,

  padding: '15px 12px',

  border: `1px solid ${theme.palette.primary.dark}`,
  borderRadius: '15px',
}));

export const SearchBarInput = styled(TextField)(({ theme }) => ({
  width: '100%',
  '.MuiInputBase-root': {
    padding: '6px 5px',

    borderRadius: '15px',

    fontWeight: 600,
    fontSize: '15px',
    color: theme.palette.text.secondary,
  },
  '.MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${theme.palette.primary.dark}`,
  },
}));

export const JobWrapper = styled(Box)(({ theme }) => ({
  width: '100%',

  maxWidth: '335px',

  height: 'max-content',
  minHeight: '200px',
  maxHeight: '300px',

  margin: '5px 0px',

  '@media (min-width:1200px) and (max-width:1330px)': {
    maxWidth: '265px',
  },

  '@media (min-width:1330px) and (max-width:1460px)': {
    maxWidth: '310px',
  },

  '@media (min-width:1700px)': {
    maxWidth: '375px',
  },

  [theme.breakpoints.between('md', 'lg')]: {
    maxWidth: '335px',
  },
}));

export const JobImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'calc(100% * 0.6)',

  display: 'block',
  marginLeft: 'auto',
  marginRight: 'auto',

  borderRadius: '20px',

  cursor: 'pointer',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: 'calc(100% * 0.75)',
  },
}));

export const WidgetCardImage = styled('img')(({ theme }) => ({
  width: '160px',
  height: 'calc(160px * 0.75)',
  display: 'block',
  borderRadius: '20px',

  [theme.breakpoints.up('md')]: {},

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: 'calc(100% * 0.75)',
  },

  [theme.breakpoints.up('lg')]: {},
}));
export const WidgetImgPlaceholder = styled(Box)(({ theme }) => ({
  width: '160px',
  height: 'calc(160px * 0.75)',
  display: 'inline-flex',
  borderRadius: '20px',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {},

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: 'calc(100% * 0.75)',
  },

  [theme.breakpoints.up('lg')]: {},
}));

export const JobProfileImage = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  height: 'auto',

  display: 'block',
  marginLeft: 'auto',
  marginRight: 'auto',

  borderRadius: '20px',

  [theme.breakpoints.up('sm')]: {
    //height: 'calc(700px * 0.356)',
    height: '268px',
  },
  [theme.breakpoints.up('md')]: {
    //height: 'calc(700px * 0.356)',
    height: '268px',
    maxWidth: 'none',
  },
}));

export const ProfileImage = styled('img')(({ theme }) => ({
  width: '60px',
  height: '60px',

  marginRight: '0.5em',

  borderRadius: '50%',
}));

export const IconImg = styled('img')(({ theme }) => ({
  marginRight: '0.5em',
}));

export const FilledHoursBadge = styled(Box)<StyledProps>(({ theme, backgroundColor }) => ({
  lineHeight: 1.1,

  minWidth: '75px',
  minHeight: '23px',

  display: 'flex',
  alignItems: 'center',

  padding: '0.3em 1em',
  marginRRight: '1em',

  fontSize: '0.65em',
  color: 'white',

  backgroundColor: `${backgroundColor || '#4f5f71'}`,
  borderRadius: '5px',

  [theme.breakpoints.down('sm')]: {
    minWidth: '55px',
  },
  [theme.breakpoints.up('sm')]: {
    minWidth: '70px',
    marginLeft: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  [theme.breakpoints.up('md')]: {},
}));

export const StatusBadge = styled(Box)<StyledProps>(({ theme, backgroundColor }) => ({
  minHeight: '23px !important',

  display: 'inline-flex !important',
  justifyContent: 'center',
  alignItems: 'center',

  padding: '0.3em 1em',

  backgroundColor: `${backgroundColor}`, //'#3d7652',
  borderRadius: '5px',

  textAlign: 'center',
  textTransform: 'capitalize',

  fontSize: '0.65em',
  color: 'white',

  '@media (min-resolution: 1.25dppx)': {
    height: 'fit-content',
  },

  [theme.breakpoints.down('sm')]: {
    minWidth: '55px',
  },
  [theme.breakpoints.up('sm')]: {
    minWidth: '60px',
  },
  [theme.breakpoints.up('md')]: {},
}));

export const TitleBadgeWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  margin: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  '& > span.badge': {
    display: 'inline-flex',
    alignItems: 'center',
    textTransform: 'capitalize',
    backgroundColor: 'lightgrey',
    padding: '0.3em 0.6em',
    borderRadius: '5px',
  },
}));

export const PinImage = styled('img')(({ theme }) => ({
  paddingRight: '0.5em',

  [theme.breakpoints.down('md')]: {
    minHeight: '0.75em',
    width: '23px',
  },
  [theme.breakpoints.up('md')]: {
    minHeight: '1.5em',
    maxHeight: '3em',
    width: '25px',
  },
}));

export const StackContents = styled(Box)(({ theme, marginTop }) => ({
  width: '100%',
  display: 'inline-grid',
  justifyContent: 'space-between',
  gridTemplateColumns: '100%',

  [theme.breakpoints.down('md')]: {
    display: 'block',
    gridRowGap: '20px',
    marginTop: '20px',
    background: 'var(--background)',
    borderRadius: '15px',
    padding: '25px',
    border: '2px dashed #cacaca',
    maxWidth: '450px',
    margin: '20px auto',

    '& > div': {
      width: '100%',
    },
    ' & > div.display-contents > div.MuiBox-root': {
      width: '100%',
      maxWidth: 'unset',
      ' & > div.MuiInputBase-root ': {
        maxWidth: 'unset !important',
      },
    },
    ' & > div.display-contents > div.MuiFormControl-root': {
      width: '100%',
      maxWidth: 'unset',
      ' & .MuiInputBase-root ': {
        maxWidth: 'unset !important',
      },
    },
  },

  [theme.breakpoints.up('md')]: {
    margin: '20px 0 0 0',
  },
}));

export const EmpSupportCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasBorder' && prop !== 'centerText',
})<SupportCardProps>(({ theme, border, hasBorder, centerText }) => ({
  //width: '100%',
  //border: hasBorder ? `2px solid ${theme.palette.primary.light}` : 'none',
  border: `2px solid ${theme.palette.common.white}`,
  borderRadius: '20px',
  //padding: "15px",

  '& > h3': {
    textAlign: `${centerText ? 'center' : 'left'}`,
  },
  [theme.breakpoints.down('sm')]: {
    //maxWidth: '68vw',
    margin: '20px 0px',
  },
  [theme.breakpoints.up('sm')]: {},
  [theme.breakpoints.down('md')]: {
    padding: '10px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '0.7em 0.2em 0.7em 0.7em',
    // border: 'none',
    // borderRadius: '0px',
    border: `2px solid ${theme.palette.common.white}`,
    marginBottom: '10px',
  },
}));

export const CustomNavDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'isCollapsed',
})<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  width: `${isCollapsed ? '120px' : '18vw'}`,
  maxWidth: '280px',
  height: '100vh',
  maxHeight: '1000px',
  transition: 'width 0.30s ease',

  backgroundColor: 'var( --background)',
  alignItems: 'center',
  display: 'inline-flex',

  '@media (max-resolution: 1dppx) and (min-height: 1030px)': {
    width: '17vw',
    maxHeight: '1000px',
  },

  '& .MuiDrawer-paper': {
    backgroundColor: 'var( --background)',
    borderRight: 'none',
    overflow: 'hidden',
    position: 'inherit',
    maxWidth: '17.5vw',
  },

  [theme.breakpoints.up('xl')]: {
    '& .MuiDrawer-paper': {
      width: 'inherit',
      maxWidth: '265px',
    },
  },
}));

export const MeterContainer = styled(Box)(({ theme }) => ({
  width: '5vw',
  minWidth: '5vw',
  display: 'block',
  marginLeft: 'auto',
  alignSelf: 'center',

  [theme.breakpoints.down('sm')]: {
    width: '14.5vw',
    minWidth: '14.5vw',
  },

  [theme.breakpoints.between('sm', 'md')]: {
    width: '10vw',
    minWidth: '10vw',
  },

  '@media (min-width:1700px)': {
    width: '4.05vw',
    minWidth: '4.05vw',
  },
}));

export const MainDisplayModal = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isMobile' &&
    prop !== 'isTablet' &&
    prop !== 'isFormOrLandingPage' &&
    prop !== 'isCollapsed',
})<StyledProps>(
  ({
    theme,
    height,
    color,
    backgroundColor,
    isMobile,
    isTablet,
    isFormOrLandingPage,
    isCollapsed,
  }) => ({
    width: '100%',
    height: `${isFormOrLandingPage ? 'auto' : '95vh'}`,
    maxHeight: '100vh',
    position: 'relative',
    border: `${
      isFormOrLandingPage
        ? 'none'
        : isMobile || isTablet
          ? 'none'
          : `1px solid ${theme.palette.primary.dark}`
    }`,
    backgroundColor: `${
      isFormOrLandingPage ? 'transparent' : isMobile || isTablet ? 'transparent' : 'white'
    }`,
    overflow: `${isFormOrLandingPage || isCollapsed ? 'scroll' : 'hidden'}`,
    color: `${theme.palette.text.primary}`, //Main Text setting

    [theme.breakpoints.down('sm')]: {
      // height adjustments for mobile
      '@media (min-height: 550px)': {
        minHeight: `calc(${height}px * 0.73)`,
        // paddingBottom: '40px',
      },
      // height adjustments for mobile
      '@media (min-height: 650px)': {
        minHeight: `calc(${height}px * 0.75)`,
        // paddingBottom: '40px',
      },
      '@media (min-height: 700px)': {
        minHeight: `calc(${height}px * 0.74)`,
        paddingBottom: '10px',
      },
      '@media (min-height: 800px)': {
        minHeight: `calc(${height}px * 0.76)`,
        paddingBottom: '5px',
      },
      '@media (min-height: 1000px)': {
        minHeight: `calc(${height}px * 0.8)`,
        maxHeight: 'fit-content',
        paddingBottom: '5px',
      },
    },

    [theme.breakpoints.up('sm')]: {
      //everything tablet upwards
      maxWidth: `${isFormOrLandingPage ? '100%' : '90%'}`,
      marginLeft: 'auto',
      marginRight: 'auto',
      '@media (min-height: 1000px)': {
        minHeight: `calc(${height}px * 0.81)`,
      },
      '@media (min-height: 1080px)': {
        height: '970px',
        minHeight: '970px',
      },
      '@media (min-height: 1080px) and (min-width: 2000px)': {
        height: '90vh',
        minHeight: 'initial',
      },
    },

    [theme.breakpoints.down('md')]: {
      flexGrow: 1,
      height: 'max-content !important',
      overflow: 'scroll',
      borderRadius: '0px',
      //maxHeight: '88vh', //Patricks commit

      '@media (min-height: 700px)': {
        padding: `${isFormOrLandingPage ? '0' : '0.2rem'}`,
      },
    },

    [theme.breakpoints.up('xl')]: {
      overflow: 'hidden',
    },

    [theme.breakpoints.up('md')]: {
      flexGrow: 'none',
      border: 'none',
      borderRadius: '25px',
      margin: `${isFormOrLandingPage ? '0' : '0.35rem'}`,
      padding: `${isFormOrLandingPage ? '2rem' : '1.25rem'}`,

      '@media (min-height: 750px)': {
        // overflow: `${(isFormOrLandingPage) ? 'hidden' : 'unset'}`
      },

      [`&::-webkit-scrollbar`]: {
        width: '3px',
        background: 'transparent',
        height: '0px',
      },
      '@media (min-resolution: 1.49dppx)': {
        '@media (max-height: 700px)': {
          overflow: 'none !important',
        },
      },
    },

    [`&::-webkit-scrollbar`]: {
      width: '0px', //causes ui issues when using overlays for loading (Recommendations page, loading gif has white-space right side of container if this is > 0px)
      background: 'transparent',
      height: '0px',
    },
  })
);

export const LogoWrapper = styled(Box)({
  width: '375px',
  justifyContent: 'center',
  textAlign: 'center',
});
export const LogoWrapperLandingPage = styled(LogoWrapper)({
  width: '100%',
});

export const LogoImg = styled('img')({
  width: '150px',
  height: 'auto',
  marginLeft: '-35px',
});

export const LandingPageImg = styled(LogoImg)({
  width: '100%',
  maxWidth: '250px',
  margin: '0px',
});

export const TEMPLATE = styled(Box)({});

export const WidgetWrapper = styled(Box)(({ theme }) => ({
  cursor: 'pointer',

  [theme.breakpoints.up('sm')]: {
    display: 'inline-flex',
    width: '100%',
  },
}));

export const LogoutStyledButton = styled(Button)(({ theme }) => ({
  height: '35px',
  width: '150px',
  borderRadius: '10px',

  [theme.breakpoints.down('sm')]: {
    width: '125px',
  },
  [theme.breakpoints.down('md')]: {},
  [theme.breakpoints.up('lg')]: {},
}));

export const LogoutWrapperBox = styled(Box)(({ theme }) => ({
  height: 'fit-content',
  minWidth: '120px',
  background: 'white',
  color: 'black',
  maxWidth: '450px',
  display: 'inline-grid',
  borderRadius: '10px',
  border: '2px solid lightgray',
  marginTop: '0px',
  animation: 'rotateMenu 250ms ease-in',
  marginLeft: '22%',

  [theme.breakpoints.down('sm')]: {
    minWidth: '80px',
  },
  [theme.breakpoints.down('md')]: {
    margin: '10px',
    '@media (max-width: 768px)': {
      marginTop: 'unset',
      marginRight: '-25px',
    },
  },
  [theme.breakpoints.up('lg')]: {
    marginRight: '-40px',
    marginBottom: '10px',
    '@media (max-width: 1950px)': {
      marginRight: '-37.5px',
    },
    '@media (max-width: 1900px)': {
      marginRight: '-35px',
    },
    '@media (max-width: 1850px)': {
      marginRight: '-32.5px',
    },
    '@media (max-width: 1800px)': {
      marginRight: '-30px',
    },
    '@media (max-width: 1750px)': {
      marginRight: '-27.5px',
    },
    '@media (max-width: 1700px)': {
      marginRight: '-25px',
    },
    '@media (max-width: 1650px)': {
      marginRight: '-22.5px',
    },
    '@media (max-width: 1600px)': {
      marginRight: '-20px',
    },
    '@media (max-width: 1550px)': {
      marginRight: '-17.5px',
      marginTop: '10px',
    },
    '@media (max-width: 1500px)': {
      marginRight: '-15px',
    },
    '@media (max-width: 1450px)': {
      marginRight: '-12.5px',
    },
    '@media (max-width: 1400px)': {
      marginRight: '-10px',
    },
    '@media (max-width: 1350px)': {
      marginRight: '-7.5px',
    },
    '@media (max-width: 1300px)': {
      marginRight: '-5px',
      marginTop: '20px',
    },
    '@media (max-width: 1250px)': {
      marginRight: '-2.5px',
    },
    '@media (max-width: 1200px)': {
      marginRight: '0px',
    },
    '@media (max-width: 1100px)': {
      marginRight: '-20px',
    },
  },
}));

export const CrossBrowserMultiLine = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'lineCount',
})<StyledProps>(({ theme, lineCount }) => ({
  display: 'block', //pure css
  WebkitBoxOrient: 'vertical',
  position: 'relative',

  lineHeight: '1.2 !important',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: '0 !important',

  whiteSpace: 'normal',
  [theme.breakpoints.down('md')]: {},
  [theme.breakpoints.up('md')]: {},
}));

export const MultiLineOverFlow = styled(CrossBrowserMultiLine, {
  shouldForwardProp: (prop) => prop !== 'lineCount',
})<StyledProps>(({ theme, lineCount }) => ({
  display: '-webkit-box', //if supported, if not falls back to inherited display prop.
  WebkitLineClamp: `${lineCount}`, //if supported
  height: `calc(1em * 1.2 * ${lineCount})`,
  //textWrap: 'balance',
  [theme.breakpoints.down('md')]: {},
  [theme.breakpoints.up('md')]: {},
}));
